import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import HomeHeader from '../components/HomeHeader';
import HairToDyeForTestimonials from '../components/HairToDyeForTestimonials';
import ContactSection from '../components/ContactSection';
import HeroDamask from '../assets/images/White_damask.webp';
import BlackDamask from '../assets/images/Black_damask_one.webp';
import GalleryImage from '../assets/images/GalleryImage.webp';
import ServicesImage from '../assets/images/ServicesImage.webp';

const HomePage = () => {
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  
  // Determine video source based on device - set once on mount
  const getVideoSource = () => {
    const checkIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (checkIOS) {
      // Use iOS-optimized video (Baseline profile, lower bitrate)
      console.log('Loading iOS-optimized video');
      return require('../assets/videos/HomePageVideo.mp4');
    } else {
      // Use original high-quality video for desktop/Android
      console.log('Loading desktop/Android video');
      return require('../assets/videos/HomePageVideo.mp4');
    }
  };
  
  // Set video source once when component mounts
  const [videoSource] = useState(getVideoSource());
  
  useEffect(() => {
    // Detect if device is iOS
    const checkIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(checkIOS);
    
    const video = videoRef.current;
    if (!video) return;

    // Set iOS-specific attributes
    video.setAttribute('webkit-playsinline', 'webkit-playsinline');
    video.setAttribute('playsinline', 'playsinline');
    video.defaultMuted = true;
    video.muted = true;
    
    // Try to autoplay (will work on desktop, fail on iOS)
    const attemptAutoplay = async () => {
      try {
        await video.play();
        setIsVideoPlaying(true);
        setShowPlayButton(false);
      } catch (err) {
        console.log('Autoplay blocked:', err);
        // On iOS, show play button
        if (checkIOS) {
          setShowPlayButton(true);
        }
      }
    };

    // Wait for video to be ready
    video.addEventListener('loadeddata', attemptAutoplay);
    
    // Monitor play state
    video.addEventListener('play', () => {
      setIsVideoPlaying(true);
      setShowPlayButton(false);
    });
    
    video.addEventListener('pause', () => {
      setIsVideoPlaying(false);
      if (checkIOS) {
        setShowPlayButton(true);
      }
    });

    return () => {
      video.removeEventListener('loadeddata', attemptAutoplay);
    };
  }, []);

  const handlePlayVideo = async () => {
    const video = videoRef.current;
    if (video) {
      try {
        await video.play();
        setIsVideoPlaying(true);
        setShowPlayButton(false);
      } catch (err) {
        console.error('Play failed:', err);
      }
    }
  };

  // Reusable font styles
  const fontStyles = {
    heroLuxury: {
      fontFamily: 'Quentin',
      fontWeight: 400,
      fontStyle: 'normal',
      lineHeight: '100%',
      letterSpacing: '0%',
      textAlign: 'center'
    },
    heroSubtitle: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 700,
      fontStyle: 'Bold',
      lineHeight: '100%',
      letterSpacing: '0.04em',
      textAlign: 'center',
      textTransform: 'uppercase'
    },
    heroDescription: {
      textAlign: 'center',
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 600,
      fontStyle: 'normal',
      fontSize: '16px',
      lineHeight: '156%',
      letterSpacing: '3.2px',
      textAlign: 'center',
      textTransform: 'uppercase'
    },
    heroBodyText: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 400,
      fontStyle: 'Regular',
      lineHeight: '156%',
      letterSpacing: '0%',
      textAlign: 'center'
    },
    heroButton: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 700,
      fontStyle: 'Bold',
      lineHeight: '156%',
      letterSpacing: '0.2em',
      textAlign: 'center',
      textTransform: 'uppercase',
      textDecoration: 'underline',
      textDecorationStyle: 'solid',
      color: '#C7A064'
    },
    actionButton: {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontStyle: 'Bold',
      lineHeight: '156%',
      letterSpacing: '3.2px',
      textAlign: 'center',
      textTransform: 'uppercase',
      textDecoration: 'underline',
      textDecorationStyle: 'solid',
      color: '#C7A064'
    },
    quentin: {
      fontFamily: 'Quentin',
      fontWeight: 400,
      fontStyle: 'normal',
      lineHeight: '100%',
      letterSpacing: '0%',
      textAlign: 'center'
    },
    montserratBold: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 700,
      fontStyle: 'normal',
      textTransform: 'uppercase'
    },
    montserratRegular: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 400,
      fontStyle: 'normal'
    },
    montserratSemiBold: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 600,
      fontStyle: 'SemiBold'
    }
  };

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Hair Extensions & Color in Easton, PA | Hair to Dye For</title>
        <meta 
          name="description" 
          content="Expert hair extensions, balayage, and precision color in Easton, PA. Book your custom transformation at Hair to Dye For. Consultations available." 
        />
        
        {/* Open Graph Tags for Social Media */}
        <meta property="og:title" content="Hair Extensions & Color in Easton, PA" />
        <meta 
          property="og:description" 
          content="Discover Easton's trusted salon for hair extensions, balayage, vivid color, and more. Hair to Dye For is your destination for expert styling." 
        />
        <meta property="og:url" content="https://www.hairtodyeforeaston.com" />
        <meta property="og:type" content="website" />
        
        {/* Additional recommended tags */}
        <meta name="keywords" content="hair extensions Easton PA, balayage Easton, hair color Easton, hair salon Easton PA, luxury hair extensions" />
        <link rel="canonical" href="https://www.hairtodyeforeaston.com" />
      </Helmet>
      
      {/* Header */}
      <HomeHeader />
      
  <section className="flex flex-col lg:flex-row w-full lg:h-[680px]">
  {/* Left Side - Text Content with Damask Background */}
  <div
    className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-16 relative min-h-[500px] lg:min-h-0 lg:h-full"
    style={{
      backgroundImage: `url(${HeroDamask})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}
  >
    <div className="w-full max-w-lg relative z-10 text-center">
      {/* SEO-optimized H1 - visually hidden but accessible */}
      <h1 className="sr-only">
        Luxury Hair Extensions & Color Experts in Easton, PA
      </h1>
      {/* Visual display heading */}
      <div
        className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[106px] mb-4"
        style={{ ...fontStyles.heroLuxury, color: '#C7A064' }}
        aria-hidden="true"
      >
        Luxury
      </div>
      <div className="flex justify-center">
        <p
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-4 px-4"
          style={{ ...fontStyles.heroSubtitle, color: '#000000' }}
          aria-hidden="true"
        >
          Extension & Color Lounge
        </p>
      </div>
      <p
        className="text-sm sm:text-base mb-6 px-4 mt-6 lg:mt-8"
        style={{ ...fontStyles.heroDescription, color: '#000000' }}
      >
        Seamless extensions and expert color services tailored for you.
      </p>
      <p
        className="text-sm sm:text-base mb-8 px-4 mt-4 lg:mt-6"
        style={{ ...fontStyles.heroBodyText, color: '#000000' }}
      >
        Experience what it's like to truly love your hair! Glossy beautiful color and stunning extensions are just a phone call away.
      </p>
      <a
        href="tel:+16109233530"
        className="inline-block px-4 py-2 sm:px-8 sm:py-3 text-xs sm:text-base rounded-md transition-colors whitespace-nowrap"
        style={{ ...fontStyles.heroButton }}
      >
        BOOK A FREE CONSULTATION
      </a>
    </div>
  </div>
  {/* Right Side - Video Content - FIXED HEIGHT */}
  <div className="w-full lg:w-1/2 relative overflow-hidden
    h-[300px] sm:h-[400px] md:h-[500px] lg:h-full
    bg-black">
    <video
      ref={videoRef}
      className="w-full h-full object-cover"
      muted
      loop
      playsInline
      webkit-playsinline="true"
      poster={ServicesImage}
      {...(!isIOS && { autoPlay: true })}
    >
      <source
        src={videoSource}
        type="video/mp4"
      />
      Your browser does not support the video tag.
    </video>
    {/* Play Button Overlay - Only shows on iOS when video is not playing */}
    {showPlayButton && isIOS && (
      <div
        className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
        onClick={handlePlayVideo}
      >
        <button
          className="bg-white/90 rounded-full p-6 shadow-xl transform transition-transform hover:scale-105 active:scale-95"
          onClick={handlePlayVideo}
          aria-label="Play video"
        >
          <svg
            className="w-16 h-16 text-black ml-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
          </svg>
        </button>
        <p className="absolute bottom-10 text-white text-base font-medium">
          Tap to play video
        </p>
      </div>
    )}
  </div>
</section>

      {/* About Us Section */}
      <section className="bg-black text-white relative overflow-hidden w-full min-h-[600px] lg:min-h-screen flex items-center justify-center py-16 lg:py-0">
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            backgroundImage: `url(${BlackDamask})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 1
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br text-[#F2EADC] to-transparent"></div>
        </div>
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="relative mb-8">
              <h2 className="text-[#4B3E29] text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[160px] mb-4 mx-auto"
                style={{ ...fontStyles.quentin }}>
                About Us
              </h2>
              <p className="text-[#F2EADC] text-xs sm:text-sm md:text-base absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                style={{
                  ...fontStyles.montserratRegular,
                  lineHeight: '100%',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase'
                }}>
                EASTON • SINCE 2005
              </p>
            </div>
            
            <h3 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-6 px-4"
              style={{ ...fontStyles.montserratBold, lineHeight: '110%', letterSpacing: '.04em' }}>
              TRUSTED HAIR EXPERTS<br />SINCE 2005
            </h3>
            <p className="text-sm sm:text-base text-gray-300 mb-6 px-4"
              style={{ ...fontStyles.montserratSemiBold, textTransform: 'uppercase', letterSpacing: '0.2em' }}>
              Personalized color and extension services tailored to you.
            </p>
            <p className="text-sm sm:text-base lg:text-lg text-gray-300 mb-8 px-4"
              style={{ ...fontStyles.montserratRegular, lineHeight: '156%' }}>
              Our team specializes in, lived in blonde, balayage, vivid colors and extensions, delivering results tailored to your style.
            </p>
            <Link
              to="/about"
              className="inline-block text-[#F2EADC] hover:text-[#F2EADC] text-sm sm:text-base font-medium transition-colors underline"
              style={{ ...fontStyles.actionButton }}
            >
              READ MORE
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="flex flex-col lg:flex-row w-full min-h-[600px] lg:min-h-screen">
        <div
          className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-16 relative order-2 lg:order-1"
          style={{
            backgroundImage: `url(${HeroDamask})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="w-full max-w-lg relative z-10 text-center">
            <div className="relative mb-8">
              <h2 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[160px] font-quentin text-[#F2EADC] mb-4 leading-[100%] tracking-[0%]">
                Gallery
              </h2>
              <p className="text-black text-xs sm:text-sm md:text-base absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                style={{
                  ...fontStyles.montserratRegular,
                  lineHeight: '100%',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase'
                }}>
                EASTON • SINCE 2005
              </p>
            </div>
            
            <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 px-4">
              REAL CLIENT TRANSFORMATIONS
            </h3>
            <p className="text-sm sm:text-base lg:text-xl text-gray-600 mb-6 px-4 uppercase tracking-widest"
            style={{ fontWeight: '600',fontSize:'16px' }}>
              SEE OUR ARTISTRY IN ACTION.
            </p>
            <p className="text-sm sm:text-base text-gray-700 mb-8 px-4"
             style={{ fontWeight: '400',fontSize:'16px' }}>
              Browse before and after transformations featuring extensions, color corrections, and precision cuts. Click the link to our Socials for all our latest work!
            </p>
            <Link
              to="/gallery"
              className="inline-block text-[#C7A064] hover:text-[#C7A064] text-sm sm:text-base font-medium transition-colors underline"
              style={{ ...fontStyles.actionButton }}
            >
              VIEW MORE
            </Link>
          </div>
        </div>
        <div className="w-full lg:w-1/2 bg-white flex justify-end h-[300px] sm:h-[400px] lg:h-auto order-1 lg:order-2">
          <img
            src={GalleryImage}
            alt="Gallery of hair transformations"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Artists Section */}
      <section className="bg-black text-white relative overflow-hidden w-full min-h-[600px] lg:min-h-screen flex items-center justify-center py-16 lg:py-0">
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            backgroundImage: `url(${require('../assets/images/ArtistImage.webp')})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 1
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br bg-black/50 to-transparent"></div>
        </div>
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="relative mb-8">
              <h2 className="text-[#4B3E29] text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[160px] mb-4 mx-auto"
                style={{ ...fontStyles.quentin }}>
                Artists
              </h2>
              <p className="text-[#F2EADC] text-xs sm:text-sm md:text-base absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                style={{
                  ...fontStyles.montserratRegular,
                  lineHeight: '100%',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase'
                }}>
                EASTON • SINCE 2005
              </p>
            </div>
            
            <h3 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-6 px-4"
              style={{ ...fontStyles.montserratBold, lineHeight: '110%', letterSpacing: '.04em' }}>
              MEET OUR EXPERT STYLISTS<br />SINCE 2005
            </h3>
            <p className="text-sm sm:text-base text-gray-300 mb-6 px-4 uppercase tracking-widest"
              style={{ ...fontStyles.montserratSemiBold }}>
              THE TALENT BEHIND EASTON'S MOST STUNNING HAIR TRANSFORMATIONS.
            </p>
            <p className="text-sm sm:text-base lg:text-lg text-gray-300 mb-8 px-4"
              style={{ ...fontStyles.montserratRegular, lineHeight: '156%' }}>
              At Hair To Dye For Extension & Color Lounge, our highly trained and certified stylists specialize
              in luxury hair extensions, balayage, precision cuts, and vibrant custom color.
              Each artist brings unique expertise and creativity, delivering a personalized experience that leaves you loving your hair. Learn more about your stylist's specialties, certifications, and portfolio below.
            </p>
            <Link
              to="/artists"
              className="inline-block text-[#F2EADC] hover:text-[#F2EADC] text-sm sm:text-base font-medium transition-colors underline"
              style={{ ...fontStyles.actionButton }}
            >
              VIEW ALL
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="flex flex-col lg:flex-row w-full min-h-[600px] lg:min-h-screen">
        <div
          className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-16 relative order-2 lg:order-1"
          style={{
            backgroundImage: `url(${HeroDamask})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="w-full max-w-lg relative z-10 text-center">
            <div className="relative mb-8">
              <h2 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[160px] font-quentin text-[#F2EADC] mb-4 leading-[100%] tracking-[0%]">
                Services
              </h2>
              <p className="text-black text-xs sm:text-sm md:text-base absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                style={{
                  ...fontStyles.montserratRegular,
                  lineHeight: '100%',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase'
                }}>
                EASTON • SINCE 2005
              </p>
            </div>
            
            <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 px-4">
              PROFESSIONAL HAIR SERVICES
              IN EASTON
            </h3>
            <p className="text-sm sm:text-base lg:text-xl text-gray-600 mb-6 px-4 uppercase tracking-widest"
            style={{textAlign: 'center',
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 600,
      fontStyle: 'normal',
      fontSize: '16px',
      lineHeight: '156%',
      letterSpacing: '3.2px',
      textAlign: 'center',
      textTransform: 'uppercase' }}>
              PRECISION CUTS, CUSTOM COLOR, AND PREMIUM EXTENSIONS.
            </p>
            <p className="text-sm sm:text-base text-gray-700 mb-8 px-4">
              Explore our full menu of services including balayage, hair extensions, color correction,
              treatments, and styling. Each service tailored to your needs with clear pricing and
              time estimates.
            </p>
            <Link
              to="/services"
              className="inline-block text-[#C7A064] hover:text-[#C7A064] text-sm sm:text-base font-medium transition-colors underline"
              style={{ ...fontStyles.actionButton }}
            >
              LEARN MORE
            </Link>
          </div>
        </div>
        <div className="w-full lg:w-1/2 bg-white flex justify-end h-[300px] sm:h-[400px] lg:h-auto order-1 lg:order-2">
          <img
            src={ServicesImage}
            alt="Hair cutting and styling services"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-black text-white relative overflow-hidden w-full min-h-[600px] lg:min-h-screen flex items-center justify-center py-16 lg:py-0">
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            backgroundImage: `url(${BlackDamask})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 1
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br text-[#F2EADC] to-transparent"></div>
        </div>
        <div className="relative z-10 text-center mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="relative">
            <h2 className="text-[#4B3E29] text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[160px] mb-4 mx-auto relative" 
                style={{ ...fontStyles.quentin }}>
              Testimonials
            </h2>
            <h3 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-6" 
                style={{ ...fontStyles.montserratBold, lineHeight: '110%', letterSpacing: '.04em' }}>
              WHAT OUR CLIENTS SAY
            </h3>
            <p className="text-sm sm:text-base text-gray-300 mb-6 px-4 uppercase tracking-widest" 
               style={{ ...fontStyles.montserratSemiBold }}>
              TRUSTED BY OUR CLIENTS FOR OVER A DECADE.
            </p>
            <p className="text-sm sm:text-base lg:text-lg text-gray-300 mb-8 px-4" 
               style={{ ...fontStyles.montserratRegular, lineHeight: '156%' }}>
              Hear from our happy clients and see their transformations. Featuring verified reviews from real guests
              and showcasing the quality of our hair extension and color services.
            </p>
            <a
  href="https://www.google.com/search?client=firefox-b-d&sca_esv=81be61c3dad309e5&sxsrf=AE3TifN0IC6GGwnzGVh1j8O2HymqJy0lyg:1758198692618&si=AMgyJEuzsz2NflaaWzrzdpjxXXRaJ2hfdMsbe_mSWso6src8symjwf52D61GHS9EuAe_NORTNlwArz7JtwUY-RxfvoaPQbVtwHu9WvlnWOgh6_Uk6Eqg-PkT3ThsOMNZK75_E6mD2oU9&q=Hair+To+Dye+For+Reviews&sa=X&ved=2ahUKEwi-oJLEqOKPAxVhTWwGHTiOCo0Q0bkNegQIIhAE&biw=1920&bih=878&dpr=1#lrd=0x89c46c53d289254b:0xfd5569212d3599cf,3,,,,"
  className="inline-block text-[#F2EADC] hover:text-[#F2EADC] text-sm sm:text-base font-medium transition-colors underline"
  style={{ ...fontStyles.actionButton }}
  target="_blank"
  rel="noopener noreferrer"
>
  READ MORE
</a>
          </div>
          
          <HairToDyeForTestimonials/>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />
    </div>
  );
};

export default HomePage;